import React, { memo, useEffect, useMemo, useState } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const ArrowHeadsPlugin = {
  id: "arrowheads",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const { enabled, datasetIndex = 1, arrowColor } = pluginOptions || {};
    if (!enabled) return;

    const meta = chart.getDatasetMeta(datasetIndex);
    if (!meta || meta.type !== "line") return;

    const ctx = chart.ctx;
    const points = meta.data || [];
    if (points.length < 2) return;

    const color =
      (arrowColor && String(arrowColor)) ||
      (chart.data?.datasets?.[datasetIndex]?.borderColor &&
        String(chart.data.datasets[datasetIndex].borderColor)) ||
      getPrimaryColour({ format: "rgb", opacity: 0.6 });

    const arrowSize = 7;
    const step = 1;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    for (let i = 1; i < points.length; i += step) {
      const p0 = points[i - 1];
      const p1 = points[i];
      if (!p0 || !p1) continue;

      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (!len || len < 1) continue;

      const ux = dx / len;
      const uy = dy / len;
      const ax = p1.x;
      const ay = p1.y;
      const px = -uy;
      const py = ux;
      const backX = ax - ux * arrowSize;
      const backY = ay - uy * arrowSize;
      const leftX = backX + px * (arrowSize * 0.55);
      const leftY = backY + py * (arrowSize * 0.55);
      const rightX = backX - px * (arrowSize * 0.55);
      const rightY = backY - py * (arrowSize * 0.55);

      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  },
};

ChartJS.register(ArrowHeadsPlugin);

const PoincareEllipsePlugin = {
  id: "poincareEllipse",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const {
      enabled,
      datasetIndex = 0,
      stroke,
      fill,
      confidence = 0.95,
    } = pluginOptions || {};
    if (!enabled) return;

    const ds = chart.data?.datasets?.[datasetIndex];
    const data = ds?.data || [];
    if (!Array.isArray(data) || data.length < 5) return;

    const pts = [];
    for (const p of data) {
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (Number.isFinite(x) && Number.isFinite(y)) pts.push([x, y]);
    }
    if (pts.length < 5) return;

    let mx = 0;
    let my = 0;
    for (const [x, y] of pts) {
      mx += x;
      my += y;
    }
    mx /= pts.length;
    my /= pts.length;

    let sxx = 0;
    let syy = 0;
    let sxy = 0;
    for (const [x, y] of pts) {
      const dx = x - mx;
      const dy = y - my;
      sxx += dx * dx;
      syy += dy * dy;
      sxy += dx * dy;
    }
    const denom = Math.max(1, pts.length - 1);
    sxx /= denom;
    syy /= denom;
    sxy /= denom;

    const trace = sxx + syy;
    const det = sxx * syy - sxy * sxy;
    const disc = Math.max(0, trace * trace - 4 * det);
    const root = Math.sqrt(disc);
    const l1 = (trace + root) / 2;
    const l2 = (trace - root) / 2;
    if (!(l1 > 0) || !(l2 > 0)) return;

    const angle =
      Math.abs(sxy) < 1e-9
        ? sxx >= syy
          ? 0
          : Math.PI / 2
        : 0.5 * Math.atan2(2 * sxy, sxx - syy);

    const chi2 = confidence >= 0.99 ? 9.21 : confidence >= 0.95 ? 5.991 : 4.605;
    const a = Math.sqrt(l1 * chi2);
    const b = Math.sqrt(l2 * chi2);

    const xScale = chart.scales?.x;
    const yScale = chart.scales?.y;
    if (!xScale || !yScale) return;

    const ctx = chart.ctx;
    const strokeCol =
      stroke || getPrimaryColour({ format: "rgb", opacity: 0.9 });
    const fillCol = fill || getPrimaryColour({ format: "rgb", opacity: 0.12 });
    const cosT = Math.cos(angle);
    const sinT = Math.sin(angle);

    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = strokeCol;
    ctx.fillStyle = fillCol;
    ctx.beginPath();

    const steps = 128;
    for (let i = 0; i <= steps; i += 1) {
      const t = (i / steps) * Math.PI * 2;
      const ex = a * Math.cos(t);
      const ey = b * Math.sin(t);
      const dx = ex * cosT - ey * sinT;
      const dy = ex * sinT + ey * cosT;
      const px = xScale.getPixelForValue(mx + dx);
      const py = yScale.getPixelForValue(my + dy);

      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }

    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(PoincareEllipsePlugin);

const DEFAULT_AXIS_MIN = 0;
const DEFAULT_AXIS_MAX = 2000;

function clamp(v, lo = DEFAULT_AXIS_MIN, hi = DEFAULT_AXIS_MAX) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(hi, Math.max(lo, n));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpColor(c0, c1, t) {
  return [
    Math.round(lerp(c0[0], c1[0], t)),
    Math.round(lerp(c0[1], c1[1], t)),
    Math.round(lerp(c0[2], c1[2], t)),
  ];
}

function heatRampRgb(t) {
  const stops = [
    { t: 0.0, c: [0, 84, 255] },
    { t: 0.25, c: [0, 220, 255] },
    { t: 0.5, c: [0, 200, 70] },
    { t: 0.75, c: [255, 210, 0] },
    { t: 1.0, c: [255, 60, 0] },
  ];

  if (t <= 0) return stops[0].c;
  if (t >= 1) return stops[stops.length - 1].c;

  for (let i = 1; i < stops.length; i += 1) {
    const a = stops[i - 1];
    const b = stops[i];
    if (t <= b.t) {
      const local = (t - a.t) / (b.t - a.t);
      return lerpColor(a.c, b.c, local);
    }
  }
  return stops[stops.length - 1].c;
}

function buildHeatmapColors(points) {
  const BIN_MS = 25;
  const bins = new Map();

  const keyOf = (p) =>
    `${Math.floor(p.x / BIN_MS)}:${Math.floor(p.y / BIN_MS)}`;

  for (const p of points) {
    const k = keyOf(p);
    bins.set(k, (bins.get(k) || 0) + 1);
  }

  let maxCount = 1;
  for (const c of bins.values()) maxCount = Math.max(maxCount, c);

  const BASE_ALPHA = 0.12;
  const EXTRA_ALPHA = 0.28;

  return points.map((p) => {
    const c = bins.get(keyOf(p)) || 1;
    const t = Math.min(1, Math.max(0, c / maxCount));
    const tBoost = Math.pow(t, 0.55);
    const [r, g, b] = heatRampRgb(tBoost);
    const alpha = Math.min(0.7, BASE_ALPHA + EXTRA_ALPHA * tBoost);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  });
}

function PoincareChart({
  x = [],
  y = [],
  height = 260,
  initialMode = "basic",
  xTitle = "RR_n (ms)",
  yTitle = "RR_n+1 (ms)",
  axisMin = DEFAULT_AXIS_MIN,
  axisMax = DEFAULT_AXIS_MAX,
}) {
  const primary = getPrimaryColour({ format: "rgb" });
  const primary60 = getPrimaryColour({ format: "rgb", opacity: 0.6 });
  const primary90 = getPrimaryColour({ format: "rgb", opacity: 0.9 });
  const primary12 = getPrimaryColour({ format: "rgb", opacity: 0.12 });
  const [mode, setMode] = useState(initialMode);

  const nTotalRaw = Math.min(
    Array.isArray(x) ? x.length : 0,
    Array.isArray(y) ? y.length : 0,
  );

  const pointsAll = useMemo(() => {
    if (!nTotalRaw) return [];
    const pts = [];
    for (let i = 0; i < nTotalRaw; i += 1) {
      const px = clamp(x[i], axisMin, axisMax);
      const py = clamp(y[i], axisMin, axisMax);
      if (px == null || py == null) continue;
      pts.push({ x: px, y: py });
    }
    return pts;
  }, [x, y, nTotalRaw, axisMin, axisMax]);

  const nTotal = pointsAll.length;
  const [nVisible, setNVisible] = useState(() => Math.max(2, nTotal || 2));

  useEffect(() => {
    setNVisible((prev) => Math.max(2, Math.min(prev, nTotal || 2)));
  }, [nTotal]);

  const points = useMemo(
    () => pointsAll.slice(0, nVisible),
    [pointsAll, nVisible],
  );

  const pointColors = useMemo(() => {
    if (mode !== "heatmap") return null;
    return buildHeatmapColors(points);
  }, [mode, points]);

  const datasets = useMemo(() => {
    const isHeat = mode === "heatmap";
    const isLinked = mode === "linked";

    const scatterDs = {
      type: "scatter",
      label: "Poincaré",
      data: points,
      pointRadius: isHeat ? 4 : 3,
      pointHoverRadius: 6,
      pointBorderWidth: isHeat ? 0 : 1,
      pointBackgroundColor: isHeat ? pointColors : primary,
      pointBorderColor: isHeat ? "rgba(0,0,0,0)" : primary,
    };

    if (!isLinked) return [scatterDs];

    const lineDs = {
      type: "line",
      label: "Link",
      data: points,
      showLine: true,
      borderColor: primary60,
      borderWidth: 1,
      pointRadius: 0,
      tension: 0,
    };

    return [lineDs, scatterDs];
  }, [points, mode, primary, primary60, pointColors]);

  const dataObj = useMemo(() => ({ datasets }), [datasets]);
  const hasData = nTotal >= 2;
  const plotFrameMaxWidth = Math.max(
    320,
    Math.round((Number(height) || 260) * 2),
  );
  const showEllipse = mode === "ellipse";

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
        arrowheads: {
          enabled: mode === "linked",
          datasetIndex: 0,
          arrowColor: primary60,
        },
        poincareEllipse: {
          enabled: showEllipse,
          datasetIndex: 0,
          stroke: primary90,
          fill: primary12,
          confidence: 0.95,
        },
      },
      scales: {
        x: {
          type: "linear",
          min: axisMin,
          max: axisMax,
          title: { display: true, text: xTitle },
          grid: { color: "rgba(0,0,0,0.06)" },
          border: { color: "rgba(0,0,0,0.2)" },
        },
        y: {
          type: "linear",
          min: axisMin,
          max: axisMax,
          title: { display: true, text: yTitle },
          grid: { color: "rgba(0,0,0,0.06)" },
          border: { color: "rgba(0,0,0,0.2)" },
        },
      },
      animation: { duration: 250 },
    }),
    [
      mode,
      showEllipse,
      primary60,
      primary90,
      primary12,
      axisMin,
      axisMax,
      xTitle,
      yTitle,
    ],
  );

  const optionsFinal = useMemo(() => {
    if (mode !== "ellipse") return options;
    const dsIndex = mode === "ellipse" && datasets.length === 2 ? 1 : 0;
    return {
      ...options,
      plugins: {
        ...options.plugins,
        poincareEllipse: {
          ...(options.plugins?.poincareEllipse || {}),
          datasetIndex: dsIndex,
        },
      },
    };
  }, [mode, options, datasets.length]);

  return (
    <div className="w-full flex flex-col gap-1 py-2">
      <select
        className="outline-none px-2 bg-transparent"
        value={mode}
        onChange={(e) => setMode(e.target.value)}
      >
        <option value="basic">Standard</option>
        <option value="ellipse">Standard + Ellipse</option>
        <option value="heatmap">Heatmap</option>
        <option value="linked">Linked</option>
      </select>

      <div
        className="mx-auto w-full"
        style={{ maxWidth: `${plotFrameMaxWidth}px` }}
        data-testid="poincare-chart-frame"
      >
        <div
          className="relative w-full aspect-square"
          data-testid="poincare-chart-plot"
        >
          {hasData ? (
            <Scatter data={dataObj} options={optionsFinal} />
          ) : (
            <div className="w-full h-full flex items-center justify-center muted-text">
              No poincaré data
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="muted-text">
            {hasData ? `${nVisible}/${nTotal}` : "0/0"}
          </span>
        </div>
        <input
          type="range"
          min={2}
          max={Math.max(2, nTotal)}
          value={Math.min(nVisible, Math.max(2, nTotal))}
          onChange={(e) => setNVisible(Number(e.target.value))}
          className="custom-range w-full"
          disabled={!hasData}
        />
      </div>
    </div>
  );
}

export default memo(PoincareChart);
